// Helpers for the evening session's "Close the day" step and the week / month /
// season boundary sessions (Book ruling 1.6, 1.11). Pure date + ledger maths and
// one loader that rebuilds the FLO river model the same way the NUMA widget does,
// so NUMA's three greens use the river's totalLoad and baseline (not flo.load).
import type { SupabaseClient } from '@supabase/supabase-js';
import { buildRiverModel, type FloRiverList, type FloRiverTask, type RiverModel } from '../lib/flo-weights';
import { dayPoints, isOrderedDay, type PointsLedger } from './gameSettings';

export async function loadRiverModel(supabase: SupabaseClient, userId: string): Promise<RiverModel> {
  const [tasksRes, listsRes, settingsRes] = await Promise.all([
    supabase
      .from('tasks')
      .select('id, list_id, group_id, sg1_id, sg2_id, value_selections, default_priority, scheduled, category, created_at, title, done, unlisted, tabled, updated_at, notes')
      .eq('user_id', userId),
    supabase.from('task_lists').select('id, name, olmps_sector').eq('user_id', userId),
    supabase.from('user_settings').select('groups, value_settings, unscheduled_task_priority_bonus').eq('user_id', userId).maybeSingle(),
  ]);
  if (tasksRes.error || listsRes.error || settingsRes.error) {
    throw new Error(tasksRes.error?.message || listsRes.error?.message || settingsRes.error?.message);
  }
  const tasks: FloRiverTask[] = ((tasksRes.data ?? []) as any[]).map((tk) => ({
    id: tk.id,
    title: tk.title ?? '',
    listId: tk.list_id,
    category: tk.category,
    groupId: tk.group_id,
    sg1Id: tk.sg1_id,
    sg2Id: tk.sg2_id,
    valueSelections: tk.value_selections,
    defaultPriority: tk.default_priority,
    etc: null,
    scheduled: tk.scheduled,
    done: !!tk.done,
    unlisted: !!tk.unlisted,
    createdAt: tk.created_at,
    updatedAt: tk.updated_at,
    isProjectLinked: false,
    tabled: !!tk.tabled,
    notes: tk.notes ?? null,
  }));
  const lists: FloRiverList[] = ((listsRes.data ?? []) as any[]).map((l) => ({ id: l.id, name: l.name, sector: l.olmps_sector ?? null }));
  const s = settingsRes.data as { groups?: unknown; value_settings?: unknown; unscheduled_task_priority_bonus?: number } | null;
  return buildRiverModel(
    tasks,
    lists,
    (s?.groups ?? []) as any[],
    (s?.value_settings ?? []) as any[],
    s?.unscheduled_task_priority_bonus ?? 1,
  );
}

/** Last calendar day of its month. */
export function isMonthEnd(d: Date): boolean {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getDate() === 1;
}

/** Last day of a season: a month end where (month number) is a multiple of the
 *  season length — seasons are counted from January. */
export function isSeasonEnd(d: Date, seasonMonths: number): boolean {
  return isMonthEnd(d) && (d.getMonth() + 1) % Math.max(1, seasonMonths) === 0;
}

export interface PeriodSummary {
  /** Days with the evening session closed. */
  closedDays: number;
  /** Days with all five ORDER rows green. */
  orderedDays: number;
  points: number;
}

/** Roll the ledger up over the `months` calendar months ending with `now`'s. */
export function periodSummary(ledger: PointsLedger, now: Date, months: number): PeriodSummary {
  const keys = new Set<string>();
  for (let i = 0; i < Math.max(1, months); i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    keys.add(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  let closedDays = 0;
  let orderedDays = 0;
  let points = 0;
  for (const [k, day] of Object.entries(ledger)) {
    if (!keys.has(k.slice(0, 7))) continue;
    if (day.eveningSession) closedDays++;
    if (isOrderedDay(day)) orderedDays++;
    points += dayPoints(day);
  }
  return { closedDays, orderedDays, points };
}

/** Every manual completion mark the user has ever ticked — the suggestion list. */
export function knownManualMarks(ledger: PointsLedger): string[] {
  const out = new Set<string>();
  for (const day of Object.values(ledger)) for (const m of day.manualMarks ?? []) out.add(m);
  return [...out].sort();
}
